/**
 * CompanyDirectoryPage — Phase 4
 * ───────────────────────────────
 * Public directory of businesses on CARTAL. Search by company name, ad
 * number, filter by category, verified-only, official agencies.
 * URL: /companies
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BadgeCheck, Search, SlidersHorizontal } from "lucide-react";
import { apiClient } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { FEATURES } from "../../lib/features";

const CATEGORY_LABELS_AR = {
  dealer: "معارض السيارات",
  official_agency: "الوكالات الرسمية",
  insurance_company: "شركات التأمين",
  finance_company: "شركات التمويل",
  rental_company: "شركات التأجير",
  workshop: "الورش",
  parts_store: "قطع الغيار",
  tire_battery_shop: "الإطارات والبطاريات",
  inspection_center: "مراكز الفحص",
  ...(FEATURES.AUCTIONS_ENABLED ? { auction_house: "دور المزادات" } : {}),
};

export default function CompanyDirectoryPage() {
  const { lang } = useI18n();
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [category, setCategory] = useState(searchParams.get("category") || "");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { limit: 48 };
    if (q) params.q = q;
    if (category) params.category = category;
    if (verifiedOnly) params.verified_only = true;
    apiClient.get("/platform/public/companies", { params })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [q, category, verifiedOnly]);

  const chips = useMemo(() => Object.entries(CATEGORY_LABELS_AR), []);

  return (
    <div className="min-h-[calc(100vh-72px)] bg-[#fafaf7]" data-testid="company-directory">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
        <div className="mb-6">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8a6f2e] font-semibold">
            {lang === "ar" ? "دليل الشركات" : "Company Directory"}
          </div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-[#0a0a0a] mt-1">
            {lang === "ar" ? "اكتشف الأنشطة الموثّقة على كارتال" : "Discover verified businesses on CARTAL"}
          </h1>
          <p className="text-[#525252] mt-2 max-w-2xl text-sm">
            {lang === "ar"
              ? "ابحث بالاسم أو برقم الإعلان (مثال: CAR-00000123)، وفلتر حسب الفئة أو حالة التوثيق."
              : "Search by company name or ad number (e.g. CAR-00000123). Filter by category or verification."}
          </p>
        </div>

        <div className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a8a8a]" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={lang === "ar" ? "اسم الشركة أو رقم الإعلان..." : "Company name or ad number..."}
                className="w-full ps-10 pe-4 py-2.5 rounded-full border border-[#0a0a0a]/10 text-sm bg-[#fafaf7]"
                data-testid="directory-search"
              />
            </div>
            <label className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#0a0a0a] cursor-pointer">
              <input type="checkbox" checked={verifiedOnly} onChange={(e) => setVerifiedOnly(e.target.checked)}
                     data-testid="filter-verified" />
              {lang === "ar" ? "موثّق فقط" : "Verified only"}
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <SlidersHorizontal className="w-3.5 h-3.5 text-[#8a8a8a]" />
            <button type="button" onClick={() => setCategory("")}
                    className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${!category ? "bg-[#0a0a0a] text-[#f7eecd]" : "bg-[#f7f4ea] text-[#525252]"}`}
                    data-testid="cat-all">
              {lang === "ar" ? "الكل" : "All"}
            </button>
            {chips.map(([key, ar]) => (
              <button type="button" key={key} onClick={() => setCategory(key)}
                      className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${category === key ? "bg-[#0a0a0a] text-[#f7eecd]" : "bg-[#f7f4ea] text-[#525252] hover:bg-[#f0eee7]"}`}
                      data-testid={`cat-${key}`}>
                {ar}
              </button>
            ))}
          </div>
        </div>

        <div className="text-[12px] text-[#8a8a8a] mb-3">
          {loading ? "…" : `${data.total} ${lang === "ar" ? "نتيجة" : "results"}`}
        </div>

        {!loading && data.items.length === 0 ? (
          <div className="rounded-2xl bg-white border border-dashed border-[#0a0a0a]/10 p-12 text-center text-[#8a8a8a]">
            {lang === "ar" ? "لا توجد شركات مطابقة" : "No companies match your filters"}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {data.items.map((b) => (
              <Link key={b.id} to={`/company/${b.slug || b.id}`}
                    className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] overflow-hidden hover:shadow-md hover:border-[#c9a55c]/40 transition-all"
                    data-testid={`dir-card-${b.id}`}>
                <div className="h-20 relative"
                     style={{ background: b.cover_url ? `url(${b.cover_url}) center/cover` : "linear-gradient(135deg,#0a0a0a,#1f1614)" }}>
                  <div className="absolute -bottom-6 start-3 w-12 h-12 rounded-xl border-2 border-white overflow-hidden shadow-md"
                       style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)" }}>
                    {b.logo_url ? <img src={b.logo_url} alt="" className="w-full h-full object-cover" /> : (
                      <div className="w-full h-full flex items-center justify-center text-lg font-bold text-[#0a0a0a]">
                        {(b.name_ar || "?").charAt(0)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="pt-7 pb-3 px-3">
                  <div className="flex items-center gap-1">
                    <div className="text-sm font-bold text-[#0a0a0a] truncate">{b.name_ar}</div>
                    {b.verification_status === "verified" && (
                      <BadgeCheck className="w-3.5 h-3.5 shrink-0" style={{ color: "#c9a55c" }} />
                    )}
                  </div>
                  <div className="text-[10px] text-[#8a8a8a]">{CATEGORY_LABELS_AR[b.category] || b.category}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
